import { useGSAP } from '@gsap/react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { ArrowRight } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { campaignThemes } from '../data/campaignThemes';
import { heroScene, portrait, publicHeadline } from '../data/content';
import { ResponsiveImage } from './ResponsiveImage';

gsap.registerPlugin(ScrollTrigger, useGSAP);

export function HomeHero() {
  const hero = useRef<HTMLElement>(null);
  const reducedMotion = useReducedMotion();
  const [promiseIndex, setPromiseIndex] = useState(0);
  const [promiseActive, setPromiseActive] = useState(true);
  const promise = campaignThemes[promiseIndex];
  const PromiseIcon = promise.icon;

  useEffect(() => {
    const element = hero.current;
    if (!element) return;
    let intersecting = true;
    const update = () => setPromiseActive(intersecting && !document.hidden);
    const observer = new IntersectionObserver(([entry]) => {
      intersecting = entry.isIntersecting;
      update();
    });
    observer.observe(element);
    document.addEventListener('visibilitychange', update);
    return () => {
      observer.disconnect();
      document.removeEventListener('visibilitychange', update);
    };
  }, []);

  useEffect(() => {
    if (reducedMotion || !promiseActive || window.matchMedia('(max-width: 800px)').matches) return;
    const interval = window.setInterval(
      () => setPromiseIndex((current) => (current + 1) % campaignThemes.length),
      2400,
    );
    return () => window.clearInterval(interval);
  }, [promiseActive, reducedMotion]);

  useGSAP(
    () => {
      const media = gsap.matchMedia();
      media.add(
        {
          desktop: '(min-width: 900px)',
          reduced: '(prefers-reduced-motion: reduce)',
        },
        (context) => {
          if (context.conditions?.reduced) return;

          gsap
            .timeline({ defaults: { ease: 'power4.out' } })
            .from('[data-hero-background]', { scale: 1.035, duration: 0.9 }, 0)
            .from('[data-hero-line]', { yPercent: 108, duration: 0.78, stagger: 0.075 }, 0.06)
            .from('[data-hero-support]', { autoAlpha: 0, y: 16, duration: 0.58 }, 0.34)
            .from('[data-hero-portrait]', { autoAlpha: 0, xPercent: 6, duration: 0.82 }, 0.1)
            .from('[data-hero-cta]', { autoAlpha: 0, y: 12, duration: 0.5 }, 0.48)
            .from('[data-hero-promises]', { autoAlpha: 0, y: 10, duration: 0.5 }, 0.6);

          gsap.to('[data-hero-portrait]', {
            y: 18,
            ease: 'none',
            scrollTrigger: {
              trigger: hero.current,
              start: 'top top',
              end: 'bottom top',
              scrub: 0.7,
            },
          });
        },
      );

      return () => media.revert();
    },
    { scope: hero },
  );

  return (
    <section ref={hero} className="home-hero" aria-labelledby="home-title">
      <div className="home-hero__background" data-hero-background aria-hidden="true">
        <ResponsiveImage media={heroScene} priority alt="" sizes="100vw" />
      </div>
      <div className="home-hero__veil" aria-hidden="true" />

      <div className="shell home-hero__grid">
        <div className="home-hero__copy">
          <p className="home-hero__eyebrow" data-hero-support>
            Trabalho que resulta<br />em uma Palmas mais forte.
          </p>
          <h1 id="home-title">
            <span className="line-mask hero-value"><span data-hero-line>{publicHeadline.label}</span></span>
            <span className="line-mask"><span data-hero-line>EM RECURSOS</span></span>
            <span className="line-mask"><span data-hero-line>PÚBLICOS PARA</span></span>
            <span className="line-mask"><span data-hero-line>PALMAS.</span></span>
          </h1>
          <div className="home-hero__support" data-hero-support>
            <p>
              Obras, equipamentos e estruturas organizados por área.<br />
            </p>
          </div>
          <a className="home-hero__cta" href="#investimentos" data-hero-cta>
            Ver projetos e investimentos <ArrowRight aria-hidden="true" />
          </a>
          <div className="home-hero__promises" data-hero-promises aria-label="Mais Educação, Saúde, Infraestrutura e Oportunidades">
            <AnimatePresence mode="wait" initial={false}>
              <motion.span
                className="home-hero__promise"
                key={promise.label}
                initial={reducedMotion ? false : { opacity: 0, y: 9, filter: 'blur(5px)' }}
                animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                exit={reducedMotion ? undefined : { opacity: 0, y: -9, filter: 'blur(5px)' }}
                transition={{ duration: 0.42, ease: [0.16, 1, 0.3, 1] }}
                aria-hidden="true"
              >
                <PromiseIcon />
                {promise.label}
              </motion.span>
            </AnimatePresence>
          </div>
        </div>

        <div className="home-hero__portrait" data-hero-portrait>
          <ResponsiveImage media={portrait} priority sizes="(max-width: 800px) 78vw, 58vw" />
        </div>
      </div>
    </section>
  );
}
